import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import moment from "moment";
import { Badge, Progress } from "antd";

import "./UserCard.css";

function UserCard({ dashboardStore, orgId, userCode, orgDashboardStore }) {
  const [loading, setLoading] = useState(true);
  const [heartRate, setHeartRate] = useState(null);
  const [counter, setCounter] = useState(0);
  const [timeAgo, setTimeAgo] = useState("");
  const [hrv, setHrv] = useState(null);
  const [motion, setMotion] = useState(null);
  const [color, setColor] = useState("gray");
  const [alertLabel, setAlertLabel] = useState(null);
  const [introspection, setIntrospection] = useState(null);
  const [progress, setProgress] = useState(null);

  const fetchData = async () => {
    dashboardStore.addSubscription(userCode);
  };

  const updateCounter = () => {
    setCounter(Math.random());
    setTimeout(updateCounter, 1000);
  };

  useEffect(() => {
    setLoading(false);
    fetchData();
    setTimeout(updateCounter, 1000);
  }, []);

  const userClicked = () => {
    window.open(`/detail-stress-chart?userId=${userCode}`, "_blank");
  };

  const colorFromStatus = (status) => {
    if (status === "red" || status === "green" || status === "yellow") {
      return status;
    }
    return "gray";
  };

  const colorForHrv = (hrv) => {
    let color = "gray";
    if (!hrv || !orgDashboardStore.isActive[userCode]) {
      return color;
    }
    const classification = hrv.classification;

    if (classification === "no-data" || classification === "unknown") {
      color = "gray";
    } else if (classification === "low" || classification === "high") {
      color = "yellow";
    } else if (classification === "normal") {
      color = "green";
    }
    return color;
  };

  const colorForHeartRate = (heartrate) => {
    let color = "gray";
    if (!heartrate || !orgDashboardStore.isActive[userCode]) {
      return color;
    }

    const classification = heartrate.classification;

    if (classification === "no-data" || classification === "unknown") {
      color = "gray";
    } else if (classification === "high" || classification === "low") {
      color = "yellow";
    } else if (
      classification === "very-low" ||
      classification === "very-high"
    ) {
      color = "red";
    } else if (classification === "normal") {
      color = "green";
    }
    return color;
  };

  const colorForMotion = (motion) => {
    let color = "gray";
    if (!motion || !orgDashboardStore.isActive[userCode]) {
      return color;
    }
    const classification = motion.classification;

    if (classification === "no-data" || classification === "unknown") {
      color = "gray";
    } else if (
      classification === "none" ||
      classification === "limited" ||
      classification === "some"
    ) {
      color = "green";
    } else if (classification === "high") {
      color = "yellow";
    }
    return color;
  };

  useEffect(() => {
    let color = "gray";
    if (orgDashboardStore.isActive[userCode]) {
      if (orgDashboardStore.status[userCode]) {
        const status = orgDashboardStore.status[userCode];
        color = colorFromStatus(status);
      }
    }
    if (orgDashboardStore.alertLabel[userCode]) {
      const alertLabel = orgDashboardStore.alertLabel[userCode];
      setAlertLabel(alertLabel);
    }

    if (orgDashboardStore.introspection[userCode]) {
      const introspection = orgDashboardStore.introspection[userCode];
      setIntrospection(introspection);
    }
    setColor(color);
  }, [
    userCode,
    orgDashboardStore.isActive,
    orgDashboardStore.status,
    orgDashboardStore.counter,
    orgDashboardStore.alertLabel,
    orgDashboardStore.introspection,
    timeAgo,
  ]);

  useEffect(() => {
    const heartRate = orgDashboardStore.heartRate[userCode];
    setHeartRate(heartRate);
  }, [userCode, orgDashboardStore.heartRate, orgDashboardStore.counter]);

  useEffect(() => {
    const hrv = orgDashboardStore.hrv[userCode];
    setHrv(hrv);
  }, [userCode, orgDashboardStore.hrv, orgDashboardStore.counter]);

  useEffect(() => {
    const motion = orgDashboardStore.motion[userCode];
    setMotion(motion);
  }, [userCode, orgDashboardStore.motion, orgDashboardStore.counter]);

  const timeSince = (date) => {
    var seconds = Math.max(0, Math.floor((new Date() - date) / 1000));
    var interval = seconds / 31536000;

    if (interval > 1) {
      return Math.floor(interval) + " years";
    }
    interval = seconds / 2592000;
    if (interval > 1) {
      return Math.floor(interval) + " months";
    }
    interval = seconds / 86400;
    if (interval > 1) {
      return Math.floor(interval) + " days";
    }
    interval = seconds / 3600;
    if (interval > 1) {
      return Math.floor(interval) + " hours";
    }
    interval = seconds / 60;
    if (interval > 1) {
      return Math.floor(interval) + " minutes";
    }
    return Math.floor(seconds) + " seconds";
  };

  useEffect(() => {
    if (!heartRate) {
      return;
    }
    const timeSinceString = timeSince(moment(heartRate.timestamp).toDate());
    setTimeAgo(timeSinceString);
  }, [heartRate, counter]);

  useEffect(() => {
    if (!introspection) {
      return;
    }
    const completeness = introspection["data_completeness"];
    const matched = introspection["match_percentile"];
    const progress1 = Math.min(
      1,
      completeness[0] / completeness[1] / completeness[2]
    );
    const progress2 = Math.min(1, matched[0] / matched[1] / matched[2]);
    setProgress((progress1 + progress2) / 2);
  }, [introspection, timeAgo]);

  return (
    <>
      <div className="UserCard">
        <div style={{ width: "100%" }}>
          <div className="UserCard__status">
            {orgDashboardStore.isActive[userCode] && (
              <div className="UserCard__statusIndicator">
                <div className="active"></div>
              </div>
            )}
            {!orgDashboardStore.isActive[userCode] && (
              <div className="UserCard__statusIndicator">
                <div className="inactive"></div>inactive
              </div>
            )}
          </div>
          <h1>
            <a onClick={() => userClicked()}>{userCode}</a>
          </h1>

          {/* main status */}
          <div className="UserCard__heart">
            <object
              alt={`Status ${color}`}
              data={`heart-${color}.svg`}
              width="100%"
              height="100%"
            ></object>
            <object
              alt={`Status ${color}`}
              className={
                orgDashboardStore.adminMode &&
                orgDashboardStore.isActive[userCode]
                  ? "heart"
                  : ""
              }
              style={{ marginLeft: "-120px", marginBottom: "25px" }}
              data="heart-beat-graph-svgrepo-com.svg"
              width="60%"
            ></object>
          </div>

          {/* alerts */}
          {alertLabel && (
            <div className="UserCard__alert">
              {alertLabel !== "All clear" && (
                <Badge
                  className="UserCard__alert_badge"
                  status={alertLabel === "All clear" ? "success" : "warning"}
                  text={alertLabel}
                />
              )}
              {introspection && progress < 1 && (
                <Progress
                  percent={progress * 100}
                  showInfo={false}
                  strokeWidth="3px"
                  strokeColor={introspection["color"]}
                />
              )}
            </div>
          )}

          {/* vitals */}
          <div className="UserCard__container">
            <div className="UserCard__container_col">
              Heart rate
              <div
                className={`UserCard__metric_indicator ${colorForHeartRate(
                  heartRate
                )}`}
              >
                {heartRate && heartRate.classification}
              </div>
              {heartRate && orgDashboardStore.isActive[userCode] && (
                <div className="UserCard__metric_value">
                  <div>
                    {heartRate.value}
                    <span className="UserCard__metric_value_unit">bpm</span>
                  </div>
                </div>
              )}
            </div>
            <div className="UserCard__container_col">
              HRV
              <div className={`UserCard__metric_indicator ${colorForHrv(hrv)}`}>
                {hrv && hrv.classification}
              </div>
              {orgDashboardStore.adminMode &&
                hrv &&
                orgDashboardStore.isActive[userCode] && (
                  <div className="UserCard__metric_value">
                    <div>
                      {hrv.value.toFixed(1)}
                      <span className="UserCard__metric_value_unit">sdnn</span>
                    </div>
                  </div>
                )}
            </div>
            <div className="UserCard__container_col">
              Motion
              <div
                className={`UserCard__metric_indicator ${colorForMotion(
                  motion
                )}`}
              >
                {motion && motion.classification}
              </div>
              {orgDashboardStore.adminMode &&
                motion &&
                orgDashboardStore.isActive[userCode] && (
                  <div className="UserCard__metric_value">
                    <div>
                      {motion.value.toFixed(1)}
                      <span className="UserCard__metric_value_unit">m/s2</span>
                    </div>
                  </div>
                )}
            </div>
          </div>

          <div className="clear"></div>
        </div>
      </div>
    </>
  );
}

export default observer(UserCard);
